import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "My love letter to Prettier",
  "description": "",
  "published": "2017-11-08T00:00:00.000Z",
  "redirect_from": ["/blog/love-letter-prettier/swizec/7909"],
  "categories": "Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`I've been using `}<a parentName="p" {...{
        "href": "https://github.com/prettier/prettier"
      }}>{`Prettier`}</a>{` for a month. How did I ever live without it?`}</p>
    <p>{`If you haven't heard, Prettier is an opinionated code formatter. That tells you everything and nothing about how great it is and why you might use it.`}</p>
    <p>{`Here's a gif instead 👇`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/11/prettier-magic.gif",
        "alt": null
      }}></img></p>
    <p>{`You write some nasty code, press save, and Prettier makes it look good.`}</p>
    <p>{`That might not look like much, but it changed my life. You never realize how much time you're wasting on formatting code until a machine starts doing it for you.`}</p>
    <p>{`I wish I knew how to measure the difference.`}</p>
    <p>{`One thing I do know is that it's a big enough difference that when I ran an `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/teaching-stubhub-react-redux/swizec/7860"
      }}>{`Intro to React and Redux workshop`}</a>{` with and without Prettier, people found it easier to follow when I had Prettier. You could `}<em parentName="p">{`feel`}</em>{` that they were paying more attention.`}</p>
    <p>{`Part of the reason is that my code was no longer idiosyncratic. Not in formatting, at least. There are always going to be `}<em parentName="p">{`some`}</em>{` idiosyncrasies, but Prettier resolves most of the stuff that usually trips you up when reading code.`}</p>
    <p>{`Using parentheses around single argument fat arrow functions, `}<inlineCode parentName="p">{`(a) => b`}</inlineCode>{` or `}<inlineCode parentName="p">{`a => b`}</inlineCode>{`? Who cares? Prettier fixes it.`}</p>
    <p>{`Self-closing JSX tags or not? `}<em parentName="p">{`shrug`}</em>{`. Prettier.`}</p>
    <p>{`Splitting long argument lists into many lines or one long line or just a few lines? Don't know. Prettier will do it.`}</p>
    <p>{`The amount of decisions you'll never have to make again. You'll never have to worry about in code-review. You'll never have to debate and discuss for hours on end… it's amazing. I mean look at this!`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/11/prettier-magic-2.gif",
        "alt": null
      }}></img></p>
    <p>{`Shitty code. Good code.`}</p>
    <p>{`My favorite part is never pressing enter ever again. I just type and type and type, then save and voila.`}</p>
    <h2 {...{
      "id": "how-to-introduce-prettier-at-work"
    }}>{`How to introduce Prettier at work`}</h2>
    <p><em parentName="p">{`"Ah but, Swizec, I have a big old codebase! We can't start using this now, can we?"`}</em></p>
    <p>{`Yes, you can!`}</p>
    <p>{`Here's how you do it. At least how I did it at the J.O.B after using it for 1 day at home. My mind went like this `}<em parentName="p">{`"OMG this is awesome! I'm setting this up for everyone tomorrow!"`}</em>{`.`}</p>
    <p>{`And so I did. For a codebase that's been growing since before ES6 came out. Yes, that old.`}</p>
    <p><strong parentName="p">{`1.`}</strong>{` Set up a git commit hook. `}<a parentName="p" {...{
        "href": "https://prettier.io/docs/en/precommit.html"
      }}>{`There are instructions in the Docs`}</a>{`. I used the `}<inlineCode parentName="p">{`lint-staged`}</inlineCode>{` option because it required the least setup.`}</p>
    <p><strong parentName="p">{`2.`}</strong>{` Set up your `}<inlineCode parentName="p">{`.prettierrc`}</inlineCode>{`. I like to set `}<inlineCode parentName="p">{`tabWidth: 4`}</inlineCode>{` and `}<inlineCode parentName="p">{`printWidth: 80`}</inlineCode>{`. This ensures everyone's Prettier will follow the same rules.`}</p>
    <p><strong parentName="p">{`3.`}</strong>{` You run Prettier on all your files. For us, that looked like this: `}<inlineCode parentName="p">{`prettier app/assets/**/*.js --write`}</inlineCode>{`. You'll have to change it based on where your code lives`}</p>
    <p><strong parentName="p">{`4.`}</strong>{` Submit a PR and pray it gets merged soon because anything else that gets merged before it will introduce conflicts.`}</p>
    <p><strong parentName="p">{`5.`}</strong>{` Deal with small annoyances until everybody's feature branches catch up and start using Prettier.`}</p>
    <p>{`Congratulations! Your team will never again have to debate whether to use double or single quotes!`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/11/Pokemon-celebrate.gif",
        "alt": null
      }}></img></p>
    <p>{`Oh and no, Prettier won't introduce any bugs. It's been battle tested, and you should be fine. Might want to run a quick smoke test before deploying to production though ☺️`}</p>
    <p>{`You might find some of your old code funny to look at. All modern and styled and beautiful, yet somehow odd. Odd because it doesn't use all the ES6 things you use today, but looks just like you wrote it yesterday.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/11/new-fav-prettier.gif",
        "alt": null
      }}></img></p>
    <h2 {...{
      "id": "things-i-wish-prettier-could-do-but-cant-yet"
    }}>{`Things I wish Prettier could do, but can't (yet?)`}</h2>
    <p>{`As lazy as I've become with Prettier, I want to grow even lazier. I want to be able to write not just sloppy code, but `}<em parentName="p">{`terribly`}</em>{` sloppy code and have Prettier fix it.`}</p>
    <p>{`For example, right now when I write `}<inlineCode parentName="p">{`"string'`}</inlineCode>{` (mismatched quotes), Prettier complains about a syntax error. What if it fixed the syntax error instead?`}</p>
    <p>{`Or when I get sloppy with closing my parens in a long function chain, what if Prettier could fix it instead of complaining?`}</p>
    <pre><code parentName="pre" {...{}}>{`export default withRouter(
connect(state => ({
items: state.shoppingCart.items
}))(ShoppingCart)
);
`}</code></pre>
    <p>{`See all those little parens that have to be just right? Wouldn't it be great if Prettier could just figure it out for us and close them as necessary? 😛`}</p>
    <p>{`Quote and paren matching isn't `}<em parentName="p">{`that`}</em>{` difficult to implement. I remember learning about it in a compilers course in college.`}</p>
    <p>{`It increases complexity by a lot because you have to keep track of context, but it's doable. A potentially bigger problem is that it might be hard to guess what you want as a user. Are you trying to close everything? Just one expression? `}<em parentName="p">{`shrug`}</em></p>
    <p>{`But maybe I'm the only person who wants this because everybody else uses their editor to automatically close parens as soon as they type. You know, the part where `}<inlineCode parentName="p">{`(`}</inlineCode>{` autocompletes to `}<inlineCode parentName="p">{`()`}</inlineCode>{`. That removes any need for this feature 🤔`}</p>
    <p>{`Automatic quote matching tho, I want it.`}</p>
    <p><strong parentName="p">{`PS:`}</strong>{` I now want Prettier for all the languages I use. Indenting Ruby is just such a pain. 😇`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      